import ReactDOM from "react-dom";
import { FC, CSSProperties, useMemo, ChangeEvent, useRef, useEffect, useState, RefObject, KeyboardEvent, Key, MouseEvent, MutableRefObject, } from "react";
// 组件
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import SuperTable from '../../../SuperTable';
// 方法
import classNames from "classnames";
import { filterSearchOptions, } from "../../util";
// 常量
import { DEFAULT_FIELD_NAMES, SelectDropdownActionType, SelectDropdownBaseProps, DropdownTableOption } from "../../interface";
import { TableRowSelection } from "antd/lib/table/interface";

interface SelectDropdownProps extends SelectDropdownBaseProps {
  /** 是否显示 */
  show?: boolean;
  /** 距离页面左侧的距离 */
  left?: number;
  /** 距离页面顶侧的距离 */
  top?: number;
  /** 距离页面底侧的距离 */
  bottom?: number;
  /** dropdownRef */
  dropdownRef: RefObject<HTMLDivElement>;
  /** 最小宽度 */
  minWidth: number,
  /** 点击选项 */
  onClickItem: (optionValue: Key) => void;
  /** 批量改变选中选项 */
  onChangeItems: (optionValue: Key[]) => void;
  /** 对外方法 */
  actionRef: MutableRefObject<SelectDropdownActionType>;
}

const SelectDropdown: FC<SelectDropdownProps> = props => {
  const {
    value, show, left, top, bottom, showSearch, options, columns = [], popupClassName, fieldNames, autoPage,
    loading, searchPlaceholder, isUpperCase, dropdownStyle, minWidth, listHeight, dropdownRef, tableRef,
    dropdownFooterRender, isLocalSearch, actionRef, searchDiscriminateCase, mode, pagination,
    onClickItem, onChangeItems, onSearch, getPopupContainer, onPaginationChange,
  } = props;
  const [innerOptions, setInnerOptions] = useState<DropdownTableOption[]>(); // 显示出来的选项
  const [searchValue, setSearchValue] = useState<string>(); // 搜索内容
  const inputRef = useRef<HTMLInputElement>(null); // 搜索选项输入框
  const timer = useRef<any>(); // 延时过滤选项定时器
  useEffect(() => {
    const newOptions = (searchValue && options) ? filterSearchOptions(options, searchValue, fieldNames, searchDiscriminateCase) : options;
    setInnerOptions(newOptions);
  }, [options]);
  /** 搜索内容改变 */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let filterValue = event.target.value;
    if (isUpperCase) {
      filterValue = filterValue.toUpperCase();
      event.target.value = filterValue;
    }
    setSearchValue(filterValue);
    handleFilterOptions(filterValue?.trim());
  };
  /** 清除搜索内容 */
  const handleClearSearch = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    handleEmptySearch();
    handleFilterOptions();
  };
  /**
   * 过滤选项
   * @param filterValue 过滤的内容
   * @param isSearch 是否触发onSearch回调
   * @returns 
   */
  const handleFilterOptions = (filterValue?: string, isSearch: boolean = true) => {
    if (isLocalSearch) {
      if (!options) return;
      timer.current && clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const newOptions = filterValue ? filterSearchOptions(options, filterValue, fieldNames, searchDiscriminateCase) : options;
        setInnerOptions(newOptions);
        isSearch && onSearch?.(filterValue);
      }, 300);
      return;
    }
    isSearch && onSearch?.(filterValue);
  };
  /** input搜索框键盘事件 */
  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    const keyNum = event.key;
    if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
      event.preventDefault();
    }
  }
  /** 清空搜索内容 */
  const handleEmptySearch = () => {
    setSearchValue(undefined);
    if (inputRef.current) inputRef.current.value = '';
  };
  const innerDropdownStyle: CSSProperties = useMemo(() => {
    const newDropdownStyle: CSSProperties = {
      width: minWidth,
      transformOrigin: 'center top',
      maxHeight: listHeight,
      ...dropdownStyle,
    };
    if (!show) {
      newDropdownStyle.display = 'none';
    } else {
      if (left) newDropdownStyle.left = left;
      if (top) newDropdownStyle.top = (top || 0) + 4;
      if (bottom) newDropdownStyle.bottom = bottom;
    }
    return newDropdownStyle;
  }, [show, left, top, bottom, listHeight]);
  const containerDom: HTMLElement = useMemo(() => {
    if (!getPopupContainer) return document.body;
    if (typeof getPopupContainer === 'function') return getPopupContainer();
    return getPopupContainer;
  }, [getPopupContainer]);
  const dropdownClasses = useMemo(() => {
    return classNames('yb-select-dropdown', popupClassName);
  }, [popupClassName]);
  /** 表格选项配置 */
  const rowSelection: TableRowSelection<any> = useMemo(() => {
    return {
      type: mode === "multiple" ? "checkbox" : "radio",
      selectedRowKeys: Array.isArray(value) ? value : (value ? [value] : undefined),
      onSelect: (record: any) => {
        onClickItem(record[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
      },
      onSelectAll: (_: boolean, __: any[], changeRows: any[]) => {
        onChangeItems(changeRows.map(item => item[fieldNames?.value || DEFAULT_FIELD_NAMES.value]));
      },
    };
  }, [ mode, value, ]);
  const renderHTML = (
    <div ref={dropdownRef} className={dropdownClasses} style={innerDropdownStyle} >
      {
        showSearch && (
          <div className="yb-select-dropdown-search">
            <SearchOutlined className="yb-select-dropdown-search-icon" />
            <input
              ref={inputRef}
              value={searchValue}
              className="yb-select-dropdown-search-input"
              type='text'
              autoComplete="off"
              placeholder={searchPlaceholder}
              onChange={handleChange}
              onKeyDown={handleInputKeydown}
            />
            {
              !!searchValue && (
                <CloseOutlined
                  className="yb-select-dropdown-search-close"
                  onClick={handleClearSearch}
                />
              )
            }
          </div>
        )
      }
      <div className="yb-select-dropdown-list">
        {
          loading ? (
            <div className="dropdown-list-empty">
              数据加载中
            </div>
          ) : (
            <>
              {
                innerOptions?.length ? (
                  <SuperTable
                    actionRef={tableRef}
                    wrapperClassName="not-padding"
                    sceneType="modal"
                    rowKey={fieldNames?.value || DEFAULT_FIELD_NAMES.value}
                    dataSource={innerOptions}
                    columns={columns}
                    operation={false}
                    toolBar={false}
                    pagination={pagination}
                    autoHeight={false}
                    autoPage={autoPage}
                    rowSelection={rowSelection}
                    onPaginationChange={onPaginationChange}
                  />
                ) : (
                  <div className="yb-select-dropdown-empty">
                    {
                      searchValue ? '没有已搜索内容匹配的选项' : '暂无数据'
                    }
                  </div>
                )
              }
            </>
          )
        }
      </div>
      {
        !!dropdownFooterRender && (
          <div className="yb-select-dropdown-footer">{dropdownFooterRender}</div>
        )
      }
    </div>
  );
  actionRef.current = {
    onEmptySearch: handleEmptySearch,
  };
  return ReactDOM.createPortal(renderHTML, containerDom);
};

export default SelectDropdown;
